import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Send, MessageSquare, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error("Kérjük, töltse ki az összes kötelező mezőt!");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Üzenet sikeresen elküldve!");
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Hiba történt az üzenet küldése közben. Kérjük, próbálja újra.");
    } finally {
      setSubmitting(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Cím",
      content: "1234 Budapest, Példa utca 123.",
      link: null
    },
    {
      icon: Phone,
      title: "Telefon",
      content: "+36 30 123 4567",
      link: "tel:+36301234567"
    },
    {
      icon: Mail,
      title: "Email",
      content: "info@premiumautokozmetika.hu",
      link: "mailto:info@premiumautokozmetika.hu"
    },
    {
      icon: Clock,
      title: "Nyitvatartás",
      content: "H-P: 8:00-18:00 | Sz: 9:00-14:00",
      link: null
    }
  ];

  const subjects = [
    "Árajánlat kérés",
    "Időpontfoglalás",
    "Szolgáltatás információ",
    "Reklamáció",
    "Egyéb"
  ];

  if (submitted) {
    return (
      <div data-testid="contact-success" className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md text-center"
        >
          <div className="w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-black" />
          </div>
          <h2 className="font-heading text-3xl text-[#E5E5E5] mb-4">ÜZENET ELKÜLDVE!</h2>
          <p className="text-[#A3A3A3] mb-6">
            Köszönjük megkeresését! Munkatársunk hamarosan felveszi Önnel a kapcsolatot 
            a megadott elérhetőségeken.
          </p>
          <Button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                name: "", email: "", phone: "", subject: "", message: ""
              });
            }}
            className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest hover:bg-[#B5952F] px-8 py-4 rounded-sm"
          >
            Új Üzenet
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page" className="bg-[#050505]">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-6">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4 block">
              Kapcsolat
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl text-[#E5E5E5] mb-6">
              LÉPJEN VELÜNK
              <span className="block text-[#D4AF37]">KAPCSOLATBA</span>
            </h1>
            <p className="text-[#A3A3A3] text-lg leading-relaxed">
              Kérdése van? Ajánlatot szeretne kérni? Írjon nekünk, és hamarosan 
              felvesszük Önnel a kapcsolatot!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-[#0A0A0A] border-t border-[#2A2A2A]">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#121212] border border-[#2A2A2A] p-6 text-center hover:border-[#D4AF37] transition-colors"
                data-testid={`contact-info-${index}`}
              >
                <info.icon size={28} className="text-[#D4AF37] mx-auto mb-3" />
                <h4 className="font-heading text-[#E5E5E5] mb-2">{info.title}</h4>
                {info.link ? (
                  <a href={info.link} className="text-[#A3A3A3] text-sm hover:text-[#D4AF37] transition-colors">
                    {info.content}
                  </a>
                ) : (
                  <p className="text-[#A3A3A3] text-sm">{info.content}</p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-24 md:py-32">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <motion.div {...fadeInUp}>
              <div className="bg-[#121212] border border-[#2A2A2A] p-8 md:p-10">
                <h2 className="font-heading text-2xl text-[#E5E5E5] mb-6 flex items-center gap-3">
                  <MessageSquare size={24} className="text-[#D4AF37]" />
                  Írjon Nekünk
                </h2>

                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <Label htmlFor="contact-name" className="text-[#A3A3A3] mb-2 block">Név *</Label>
                      <Input
                        id="contact-name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Teljes név"
                        data-testid="contact-input-name"
                        className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#D4AF37] text-[#E5E5E5] placeholder:text-[#666]"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="contact-phone" className="text-[#A3A3A3] mb-2 block">Telefon</Label>
                      <Input
                        id="contact-phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+36 30 123 4567"
                        data-testid="contact-input-phone"
                        className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#D4AF37] text-[#E5E5E5] placeholder:text-[#666]"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <Label htmlFor="contact-email" className="text-[#A3A3A3] mb-2 block">Email *</Label>
                    <Input
                      id="contact-email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="pelda@email.com"
                      data-testid="contact-input-email"
                      className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#D4AF37] text-[#E5E5E5] placeholder:text-[#666]"
                      required
                    />
                  </div>

                  <div className="mb-6">
                    <Label className="text-[#A3A3A3] mb-2 block">Tárgy *</Label>
                    <Select onValueChange={(value) => setFormData(prev => ({ ...prev, subject: value }))}>
                      <SelectTrigger 
                        className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#D4AF37] text-[#E5E5E5]"
                        data-testid="contact-select-subject"
                      >
                        <SelectValue placeholder="Válasszon tárgyat" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                        {subjects.map((subject) => (
                          <SelectItem 
                            key={subject} 
                            value={subject}
                            className="text-[#E5E5E5] focus:bg-[#1E1E1E] focus:text-[#D4AF37]"
                          >
                            {subject}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="mb-6">
                    <Label htmlFor="contact-message" className="text-[#A3A3A3] mb-2 block">Üzenet *</Label>
                    <Textarea
                      id="contact-message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Írja le kérdését vagy igényét..."
                      rows={5}
                      data-testid="contact-input-message"
                      className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#D4AF37] text-[#E5E5E5] placeholder:text-[#666]"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    data-testid="submit-contact"
                    className="w-full bg-[#D4AF37] text-black font-bold uppercase tracking-widest hover:bg-[#B5952F] py-4 rounded-sm"
                  >
                    {submitting ? "Küldés..." : "Üzenet Küldése"}
                    <Send className="ml-2" size={18} />
                  </Button>
                </form>
              </div>
            </motion.div>

            {/* Map */}
            <motion.div {...fadeInUp}>
              <div className="h-full min-h-[500px] border border-[#2A2A2A] overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d86239.48369406584!2d19.00497275!3d47.49433545!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4741c334d1d4cfc9%3A0x400c4290c1e1160!2sBudapest!5e0!3m2!1shu!2shu!4v1699999999999!5m2!1shu!2shu"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: "grayscale(100%) contrast(1.1)", minHeight: "500px" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Helyszín"
                  data-testid="contact-map"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-6">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <h2 className="font-heading text-3xl text-[#E5E5E5] mb-4">GYAKORI KÉRDÉSEK</h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {[
              {
                q: "Mennyi időt vesz igénybe egy teljes detailing?",
                a: "A teljes detailing általában 4-8 órát vesz igénybe az autó állapotától és méretétől függően. Kerámia bevonat esetén ez 1-2 napra bővülhet."
              },
              {
                q: "Kell-e előzetesen időpontot foglalni?",
                a: "Igen, az időpontfoglalás erősen ajánlott, hogy biztosíthassuk a megfelelő időt és figyelmet az Ön autójának. Online vagy telefonon is foglalhat."
              },
              {
                q: "Milyen fizetési módokat fogadnak el?",
                a: "Készpénz és bankkártya is elfogadott. Nagyobb összegű szolgáltatásoknál átutalás is lehetséges."
              },
              {
                q: "Van garancia a szolgáltatásokra?",
                a: "Igen, minden munkánkra garanciát vállalunk. Kerámia bevonat esetén ez akár 2-5 év is lehet a választott termék függvényében."
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#121212] border border-[#2A2A2A] p-6"
                data-testid={`faq-${index}`}
              >
                <h4 className="font-heading text-lg text-[#E5E5E5] mb-2">{faq.q}</h4>
                <p className="text-[#A3A3A3] text-sm">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
